import { useEffect, useMemo, useState, useRef } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { StepId } from './lib/utils'
import { useUnit } from 'effector-react'
import { $currentStep, userIdObtained, stepChanged, userObtained } from './onboarding.model'

import { getKeycloak } from '@artprov/design-system'
import { OnboardingService } from './lib/onboarding.service.js'

const STEPS = [
  {
    id: StepId.VERIFY_EMAIL,
    title: 'Verificá tu correo electrónico',
    status: 'pending',
  },
  {
    id: StepId.VERIFICATION_CODE,
    title: 'Ingresá el código de verificación',
    status: 'pending',
  },
  {
    id: StepId.NEW_PASSWORD,
    title: 'Generá una nueva contraseña',
    status: 'pending',
  },
  {
    id: StepId.DATOS_PERSONALES,
    title: 'Completá tu perfil',
    status: 'pending',
  },
]

export function useOnboarding() {
  // const { user } = useContext(AuthContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [isCompleted, setIsCompleted] = useState(false)
  const [isVerifying, setIsVerifying] = useState(true)

  const hasVerifiedRef = useRef(false)

  const model = useUnit({
    emitStepChange: stepChanged,
    emitUserId: userIdObtained,
    emitUserObtained: userObtained,
    currentStep: $currentStep,
  })

  // `AppRouter` ya dispara `checkKeycloakSession` (check-sso) para las rutas
  // públicas, por lo que `getKeycloak()` devuelve la instancia gestionada por
  // el design-system. Si hay sesión activa navegamos al área privada; si no,
  // delegamos en `login()` (con `redirectUri` saneado por el design-system).
  const redirectToLogin = () => {
    const kc = getKeycloak()
    if (kc?.authenticated) {
      navigate('/')
      return
    }
    kc?.login?.({ redirectUri: globalThis.location.origin })
  }

  // useEffect(() => {
  //   model.emitUserObtained(user.usuario)
  //   model.emitOldEmailObtained(user.email)
  // }, [user.usuario]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const current = searchParams.get('step')

    if (model.currentStep !== current && model.currentStep !== '') {
      const newParams = new URLSearchParams(searchParams)

      if (model.currentStep) newParams.set('step', model.currentStep)
      else newParams.delete('step')

      setSearchParams(newParams)
    }
  }, [model.currentStep]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasVerifiedRef.current) {
      setIsVerifying(false)
      return
    }
    const token = searchParams.get('token')

    if (!token) {
      redirectToLogin()
      return
    }

    OnboardingService.verify(token)
      .then((response) => {
        if (!response?.status) {
          redirectToLogin()
          return
        }

        hasVerifiedRef.current = true
        setIsVerifying(false)
        const weid = response?.id
        model.emitUserId(weid)

        const step = searchParams.get('step')
        if (searchParams.get('success') === 'true') {
          setIsCompleted(true)
          return
        }

        if (!step) {
          model.emitStepChange(StepId.VERIFY_EMAIL)
          return
        }

        model.emitStepChange(step)
      })
      .catch((error) => {
        console.error('Verify error:', error)
        redirectToLogin()
      })
  }, [searchParams, navigate])

  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setIsCompleted(true)
    }
  }, [searchParams])

  const completeOnboarding = () => {
    setSearchParams({ success: 'true' })
    setIsCompleted(true)
  }
  const steps = useMemo(() => {
    return STEPS.map((step, index) => {
      if (step.id === model.currentStep) {
        return { ...step, status: 'inProgress' }
      }

      if (STEPS.findIndex((s) => s.id === model.currentStep) > index) {
        return { ...step, status: 'completed' }
      }

      return step
    })
  }, [model.currentStep])

  return {
    completed: isCompleted,
    currentStep: model.currentStep,
    steps,
    completeOnboarding,
    isVerifying,
  }
}
